/**
 * The client protocol: what travels on the session WebSocket between the session
 * actor and the browser (architecture §1.4, §6.5, §6.6, §7.4, §8.2).
 *
 * The client renders projections and nothing else. It never computes a result, never
 * holds authoritative state, and every number it shows arrived as an event-derived
 * message below.
 */
import { z } from 'zod';
import { Cover, DiceExpr, GameMode, Locale, Lighting, Measures, NpcMood, ResultLineStyle, RollKind, RollMode, RollPart, TimeOfDay, ToneTag, Weather, AbilityId, SkillId, CheckKey } from './primitives.js';
import { BoardDiffBatch, Team, WatermarkRelease } from './scene-spec.js';
import { RollTarget } from './events.js';
/* ------------------------------------------------------------ HUD model */
/** Qualitative band; the HUD never shows a relationship number (§1.2). */
export const RelationshipBand = z.enum(['hostile', 'wary', 'neutral', 'warm']);
/** Discovery glyphs for exits, ported from the POC viewport panel (`? > ~`). */
export const DiscoveryGlyph = z.enum(['unknown', 'known', 'distant']);
export const HudClock = z.object({
    day: z.number().int().min(1),
    period: TimeOfDay,
    hour: z.number().int().min(0).max(23),
    weather: Weather,
});
export const HudPresentNpc = z.object({
    ref: z.string(),
    name: z.string(),
    /** Rotating one-line activity from the pack's activity tables. */
    activity: z.string(),
    band: RelationshipBand,
    mood: NpcMood,
    portraitAssetId: z.string().optional(),
    /** What they fight with, from their stat block: "spear", "fists" (ADR-084). */
    weapon: z.string().optional(),
    /** ADR-094: the conditions a spell has put on them. */
    conditions: z.array(z.string()).optional(),
});
export const HudExit = z.object({
    id: z.string(),
    label: z.string(),
    glyph: DiscoveryGlyph,
    leadsTo: z.string().optional(),
    /** ADR-103: how long the walk takes this character at a normal pace, when the map knows the distance. */
    minutes: z.number().int().min(0).optional(),
});
export const HudVitals = z.object({
    hp: z.number().int(),
    hpMax: z.number().int(),
    tempHp: z.number().int().min(0),
    ac: z.number().int(),
    conditions: z.array(z.object({ id: z.string(), label: z.string() })).default([]),
    exhaustion: z.number().int().min(0).max(6),
    concentration: z.object({ spellId: z.string(), label: z.string() }).nullable(),
    heroicInspiration: z.boolean(),
});
export const HudBelonging = z.object({
    instanceId: z.string(),
    label: z.string(),
    iconId: z.string(),
    qty: z.number().int().min(1),
});
/** The tone chip shown on the turn a tone tag changed a check (§3.7). */
export const HudToneChip = z.object({
    tag: ToneTag,
    /** "voz alta → Intimidação, +2, −10 com o guarda" */
    ruleLine: z.string(),
    checkSeq: z.number().int().nonnegative(),
    /** "Não era isso" is available at most three times per session. */
    undoAvailable: z.boolean(),
});
/**
 * §6.10's negotiation strip: the stake, the two positions and their gap, the
 * player's levers as cards, the NPC's as face-down slots, and the patience ring
 * counting something real. Read from the machine's state and never from prose.
 */
export const HudNegotiation = z.object({
    stake: z.string(),
    with: z.array(z.string()).min(1),
    stance: z.enum(['hostile', 'guarded', 'open', 'invested']),
    patience: z.number().int().min(0),
    patienceMax: z.number().int().min(1),
    direction: z.enum(['player_receives', 'player_pays']),
    player: z.object({ slot: z.string(), value: z.number().int() }),
    npc: z.object({ slot: z.string(), value: z.number().int() }),
    levers: z.array(z.object({ id: z.string(), label: z.string(), kind: z.string(), spent: z.boolean() })),
    /** The NPC's levers: revealed ones by label, hidden ones only as a count. */
    npcLeversKnown: z.array(z.string()),
    npcLeversHidden: z.number().int().min(0),
});
/** ADR-091: who the player is, as the HUD shows it; absent on a pod older than the builder. */
export const HudIdentity = z.object({
    name: z.string(),
    species: z.string().default(''),
    className: z.string().default(''),
    background: z.string().default(''),
    level: z.number().int().min(1),
});
/** ADR-093: the caster's slots and what is prepared, for the sheet. */
export const HudSpells = z.object({
    ability: AbilityId,
    slots: z.array(z.object({ level: z.number().int().min(1).max(9), total: z.number().int().min(0), expended: z.number().int().min(0) })).default([]),
    cantrips: z.array(z.object({ id: z.string(), name: z.string() })).default([]),
    prepared: z.array(z.object({ id: z.string(), name: z.string(), level: z.number().int().min(1) })).default([]),
    concentration: z.object({ spellId: z.string(), name: z.string() }).nullable().default(null),
});
/**
 * ADR-113: the settlement's map as the character knows it. Only known places are sent —
 * an unknown place's name and position never reach the client. `x`/`y` are fractions of
 * the image; `reveal` is the fog a seen place clears, as a fraction of the map's width.
 */
export const HudMapPlace = z.object({
    ref: z.string(),
    label: z.string(),
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    reveal: z.number().min(0).max(0.5),
    /** Where the label sits against the pin, as the map's author placed it. */
    labelAt: z.enum(['above', 'below']).default('below'),
    /** `seen`: the character stood there, and it is uncovered. `heard`: they were told of it; it is pinned in the fog. */
    known: z.enum(['seen', 'heard']),
    /** The character is here, or in one of its rooms. */
    here: z.boolean().default(false),
    /** Rooms of this place the character knows of — the kitchen, the undercroft. */
    rooms: z.array(z.object({ label: z.string(), known: z.enum(['seen', 'heard']) })).default([]),
});
export const HudMap = z.object({
    image: z.string(),
    /** Width over height, so the map is laid out before its image loads. */
    aspect: z.number().positive(),
    title: z.string(),
    places: z.array(HudMapPlace),
});
export const HudModel = z.object({
    identity: HudIdentity.optional(),
    /**
     * ADR-119: the unit this table reads distances in. On the HUD because the HUD is what
     * the table shows — the client renders the sheet and the board's scale from it, and the
     * pod renders its own engine lines through the same value.
     */
    measures: Measures.default('feet'),
    spells: HudSpells.optional(),
    mode: GameMode,
    clock: HudClock,
    locationRef: z.string(),
    locationLabel: z.string(),
    districtLabel: z.string().default(''),
    presentNpcs: z.array(HudPresentNpc).default([]),
    exits: z.array(HudExit).default([]),
    vitals: HudVitals,
    goldCp: z.number().int().min(0),
    /** The 6-slot belongings strip. */
    belongings: z.array(HudBelonging).max(6).default([]),
    objectiveLine: z.string().default(''),
    /** 0..1; shown as the danger meter, honestly (§3.5). */
    danger: z.number().min(0).max(1),
    /** Turns of silence remaining in a dialogue, never wall-clock (§1.2). */
    patience: z.number().int().min(0).nullable(),
    /** The open table, or null (§6.10). */
    negotiation: HudNegotiation.nullable().default(null),
    travelProgress: z.number().min(0).max(1).nullable(),
    lighting: Lighting,
    toneChip: HudToneChip.nullable(),
    /** "modo texto" / "voz reserva" chip; a fallback is never invisible (§7.4). */
    degradedChip: z.enum(['none', 'text_mode', 'backup_voice', 'captions_only']),
    /** ADR-113: the drawn map as the character knows it; null where the place has no map. */
    map: HudMap.nullable().default(null),
});
/* -------------------------------------------------------- character sheet */
/**
 * The sheet (§1.4, §5.2; ADR-091, ADR-093).
 *
 * `HudModel` is the turn's picture and is re-projected on every one of them. This is
 * the other half — what a character *is* rather than how it is doing — and it is sent
 * on attach and then only when it actually changes, because level, proficiency, saves
 * and the kit change on a level-up, a rest or a purchase and not on a sentence.
 *
 * The split is deliberate and the seam is lifetime, not subject: **anything that moves
 * within a fight stays on the HUD.** Current HP, temp HP, conditions, exhaustion,
 * expended slots and the tone chip are `HudVitals` and `HudSpells`; `hpMax`, AC, speed,
 * the proficiency bonus, the ability scores and the modifiers derived from them are
 * here. A client shows the two together and neither duplicates the other.
 *
 * Every modifier is computed **server-side** from the world state the engine owns —
 * §1.4's "the client renders projections and nothing else" applies to arithmetic as
 * much as to narration, and a sheet that let the browser add a proficiency bonus would
 * be a second opinion about a number the engine already has.
 *
 * Skills and abilities travel as **ids, never as labels**: `SkillId` is a closed enum
 * and §9.3 keeps the player's word for it in the locale catalogue. Trait and feat names
 * are the exception and carry their SRD strings, because the SRD's own text is the
 * content and there is no catalogue for it — the lobby's builder already shows exactly
 * these strings.
 */
/** One ability line: the score, its modifier, and the save rolled off it. */
export const SheetAbility = z.object({
    id: AbilityId,
    score: z.number().int().min(1),
    modifier: z.number().int(),
    /** ADR-091: the two saves the class is proficient in; false when the class is unknown. */
    saveProficient: z.boolean(),
    saveModifier: z.number().int(),
});
/** One skill line. The label is the client's to render (§9.3); the number is not. */
export const SheetSkill = z.object({
    id: SkillId,
    ability: AbilityId,
    proficient: z.boolean(),
    modifier: z.number().int(),
});
/** A species trait, a background or origin feat, as the SRD words it. */
export const SheetTrait = z.object({
    id: z.string().min(1),
    name: z.string(),
    blurb: z.string().default(''),
    source: z.enum(['species', 'feat']),
});
/** ADR-092: a limited-use class feature and what is left of it until a rest. */
export const SheetFeature = z.object({
    id: z.string().min(1),
    uses: z.number().int().min(0),
    max: z.number().int().min(0),
});
export const CharacterSheet = z.object({
    type: z.literal('character_sheet'),
    seq: z.number().int().nonnegative(),
    identity: z.object({
        name: z.string(),
        /** Collected by the builder (`builder.ts`'s `CharacterBuild.pronouns`); absent on a pregen. */
        pronouns: z.string().optional(),
        species: z.string().default(''),
        className: z.string().default(''),
        /** ADR-068's fixed subclass, once the level reaches it. */
        subclass: z.string().optional(),
        background: z.string().default(''),
        level: z.number().int().min(1),
    }),
    proficiencyBonus: z.number().int().min(0),
    abilities: z.array(SheetAbility),
    skills: z.array(SheetSkill),
    /** The numbers a scene boundary changes, not a turn. `hp` and its conditions are `HudVitals`. */
    combat: z.object({
        ac: z.number().int(),
        hpMax: z.number().int(),
        speedFt: z.number().int().min(0),
        initiative: z.number().int(),
        weapon: z.object({
            id: z.string(),
            label: z.string(),
            attackBonus: z.number().int(),
            damage: z.string(),
            damageType: z.string(),
        }),
        /** SRD Hit Dice: one per level, spent on a short rest. Absent means a full pool. */
        hitDice: z.object({ die: z.number().int().min(1), remaining: z.number().int().min(0), max: z.number().int().min(0) }).optional(),
    }),
    traits: z.array(SheetTrait).default([]),
    features: z.array(SheetFeature).default([]),
    /**
     * ADR-093's two standing numbers. What is *known*, *prepared* and *expended* is
     * `HudSpells`, which the HUD re-sends every turn; these two move with the level.
     */
    spellcasting: z.object({ ability: AbilityId, attackBonus: z.number().int(), saveDc: z.number().int() }).optional(),
});
/* ------------------------------------------------------ campaign and quests */
/**
 * The campaign, as the session knows it (ADR-076). Sent on attach and never again:
 * nothing in it changes while a session runs. Title and blurb are the lobby card's own
 * authored words (`CampaignCard`), and the session's place in the campaign is stated as
 * what it is — this sitting's number and, when the pack says so, how many sittings the
 * campaign expects. That is a pace the author set, not a measure of completion: nothing in
 * the engine knows how much of a campaign is left, and a percentage would be invented.
 */
export const CampaignInfo = z.object({
    type: z.literal('campaign'),
    campaignId: z.string().min(1),
    title: z.string().min(1),
    blurb: z.string(),
    sessionNumber: z.number().int().min(1),
    expectedSessions: z.number().int().min(1).optional(),
    /**
     * The game master's display name — the persona's (`Jack`, `Sophia`, ADR-080) — so the mark
     * beside the narrator's lines can carry an initial instead of the "GM" label's. Optional: a
     * session with no persona has none, and a client built before the field parses the message
     * unchanged (`z.object` strips what it does not know).
     */
    narratorName: z.string().min(1).optional(),
});
/**
 * One quest the session has entered. Deliberately little, because it is all the pack
 * gives a player: a quest machine has an id, a giver and stages whose only text is the
 * game master's brief — which names who is hiding what — so no stage id and no brief
 * crosses to the client. The compiled bundle keeps each stage's exits and drops its
 * `failStage`, so the one thing a stage can honestly say is whether the quest can still
 * move: `open` while the current stage has an exit, `closed` once it has none. A closed
 * quest is not "completed" or "failed"; the pod cannot tell which.
 */
export const QuestLogEntry = z.object({
    /** The quest machine's id: a key for a list, not a name. The pack authors no player-facing name. */
    id: z.string().min(1),
    /** Who gave it, by the name the session knows them by; absent when the session has no card for them. */
    giver: z.string().optional(),
    status: z.enum(['open', 'closed']),
});
/**
 * Where the campaign's quests stand (§4, §9.1; ADR-091). Sent on attach and again only
 * when it changes — `quest_advance` commits a handful of times a session, not once a turn.
 * The current objective is not here: it is `HudModel.objectiveLine`, re-projected every
 * turn, and a fight replaces it while it lasts.
 */
export const QuestLog = z.object({
    type: z.literal('quest_log'),
    seq: z.number().int().nonnegative(),
    /** Quests the session has entered, in the pack's authored order. A quest not yet begun is not revealed. */
    quests: z.array(QuestLogEntry),
    /**
     * ADR-091 as corrected: a campaign's levels come from milestones, and this is the whole
     * of the progress the engine can state — each level a milestone grants, and whether the
     * character has it yet. Neither quest nor stage is named, because a stage id is the plot.
     * Empty when the campaign grants no level in play.
     */
    milestones: z.array(z.object({ level: z.number().int().min(1).max(20), reached: z.boolean() })),
});
/* ------------------------------------------------------------ dice tray */
export const DiceTrayRequest = z.object({
    type: z.literal('roll_requested'),
    rollId: z.string(),
    kind: RollKind,
    /**
     * The pod's English title — "Stealth", "Attack: longsword" — kept as the fallback for a
     * client that cannot compose one. Since 2026-09-25 the client composes the title from `kind`,
     * `key` and `weapon` through its catalog, so the tray reads in the table's language the way
     * the sheet and the journal already do.
     */
    title: z.string(),
    /** The skill or ability a check or a save is on, so the client can name it (`ui.builder.skill.*`). */
    key: CheckKey.optional(),
    /**
     * The weapon an attack or a damage roll is with, by the pack's own label. It stays the
     * pack's English (ADR-059: campaign content is authored in English), as the sheet shows it.
     */
    weapon: z.string().optional(),
    expr: DiceExpr,
    parts: z.array(RollPart),
    /** Absent, or `shown: false`, renders "vs CD ?" until the die lands. */
    target: RollTarget.nullable(),
    advantage: z.enum(['none', 'adv', 'dis']),
    mode: RollMode,
    /** Spoken idle prompt after this many ms; the engine auto-rolls at `autoAfterMs`. */
    promptAfterMs: z.number().int().min(0),
    autoAfterMs: z.number().int().min(0),
    /** Whose die: the player's colour, or an enemy's quick tumble (§5.4). */
    owner: z.enum(['player', 'enemy', 'npc']),
});
/** The client's throw: the gesture is hashed locally and becomes the roll's entropy. */
export const DiceTrayThrow = z.object({
    type: z.literal('resolve_roll'),
    rollId: z.string(),
    entropyHash: z.string().regex(/^[0-9a-f]{64}$/),
    clientThrowId: z.string(),
    gesture: z.enum(['pointer', 'touch', 'shake', 'keyboard', 'voice']),
});
export const DiceTrayResolved = z.object({
    type: z.literal('roll_resolved'),
    rollId: z.string(),
    dice: z.array(z.object({
        sides: z.number().int(),
        value: z.number().int(),
        kept: z.boolean(),
    })),
    total: z.number().int(),
    target: RollTarget.nullable(),
    success: z.boolean().nullable(),
    entropySource: z.enum(['engine', 'player']),
    autoAfterTimeout: z.boolean(),
    /** "14 = 9 + 3 + 2 — falha" */
    breakdown: z.string(),
});
/** The client reports the animation resting so the engine line speaks on the landing. */
export const DiceTrayLanded = z.object({
    type: z.literal('roll_landed'),
    rollId: z.string(),
});
/** Switching one roll kind between Auto and I-roll, from the tray chip or settings. */
export const SetRollModeMessage = z.object({
    type: z.literal('set_roll_mode'),
    kind: RollKind,
    mode: RollMode,
});
/** Tray KPIs (§6.6): `RollRequested → visible` ≤ 300 ms p95. */
export const DICE_TRAY_VISIBLE_TARGET_MS = 300;
/* ------------------------------------------------------- ledger and log */
/** One Ledger row: every roll with its breakdown, mode and entropy (§5.4). */
export const LedgerRollRow = z.object({
    seq: z.number().int().nonnegative(),
    kind: RollKind,
    actor: z.string(),
    expr: DiceExpr,
    dice: z.array(z.object({ sides: z.number().int(), value: z.number().int(), kept: z.boolean() })),
    parts: z.array(RollPart),
    total: z.number().int(),
    target: RollTarget.nullable(),
    outcome: z.enum(['success', 'failure', 'hit', 'miss', 'crit', 'none']),
    mode: RollMode,
    entropySource: z.enum(['engine', 'player']),
    /** First 8 hex of the entropy hash; the whole hash is in the exported log. */
    entropyHashPrefix: z.string(),
    autoAfterTimeout: z.boolean(),
    toneApplied: z
        .object({ tag: ToneTag, ruleId: z.string(), effect: z.string() })
        .nullable(),
    /** Set when a Heroic Inspiration reroll superseded this row. */
    replacedBy: z.string().nullable(),
});
/* ---------------------------------------------------------------- journal */
/**
 * ADR-120: the journal — what the player already perceived, as typed entries.
 *
 * The pod records 85 kinds of event and most of them are the world's own bookkeeping, some
 * of it secret (a rumour's reliability, a hidden lever, who is coming). What crosses the wire
 * is a **projection**: an allowlist of 28 kinds (`apps/session-core/src/projections/journal.ts`),
 * each rendered into one of the entries below with the fields named here and no others. Every
 * object is `strict`, so a recorded payload cannot ride along: an entry carrying a field this
 * file does not name fails to parse.
 *
 * Names (`actor`, `target`, `counterpart`) are the display names the engine's own lines use,
 * resolved on the pod — a ref is a wire word and never reaches the screen. Numbers the player
 * was not told stay out: an enemy's remaining hit points are on no entry.
 */
/** ADR-120 §2: the eight families entries are listed in. A listing, not a colour group (§7). */
export const JournalFamily = z.enum(['dice', 'combat', 'harm', 'trial', 'craft', 'purse', 'road', 'sitting']);
/** Whose die it was: the three values the tray already uses (`DiceTrayRequest.owner`). */
export const RollOwner = z.enum(['player', 'enemy', 'npc']);
const journalBase = {
    /**
     * The journal's own ordinal: 1, 2, 3, with no gaps. **Not the event log's `seq`** — that
     * number counts every event, so the gaps between two entries were the hidden ones, and a
     * player who cannot read a secret could count them (found on screen, 2026-09-21: entries
     * 4, 6, 18, 19 said eleven things had happened that they were not told).
     */
    n: z.number().int().min(1),
    /** Epoch milliseconds at which the engine committed it. */
    at: z.number(),
    /**
     * ADR-120 §6: the sitting this entry belongs to. A table's journal is read across its
     * finished sessions, and `n` restarts at 1 in each, so the pair `(sessionNumber, n)` is
     * the entry's identity on screen and the panel groups by the first.
     *
     * `SessionStarted` carries a field of its own under the same name (the number of the
     * session that began). In `entry()` the kind's shape is spread after this base, so for that
     * one kind the two are one key and the kind's wins — the same number by construction, since
     * the sitting that started is the sitting the entry is in. A literal that writes the key
     * twice is a type error, not a second field.
     */
    sessionNumber: z.number().int().min(1),
};
function entry(kind, family, shape) {
    return z.object({ ...journalBase, kind: z.literal(kind), family: z.literal(family), ...shape }).strict();
}
const journalDie = z.object({ sides: z.number().int(), value: z.number().int(), kept: z.boolean() }).strict();
export const JournalRecord = z.discriminatedUnion('kind', [
    /* ADR-120 §3: one entry per roll — `RollRequested` + `RollResolved` joined on `rollId`. */
    entry('Roll', 'dice', {
        rollId: z.string(),
        owner: RollOwner,
        actor: z.string(),
        rollKind: RollKind,
        expr: z.string(),
        /* What the die was against, as the engine's line says it aloud once it lands ("18 against AC 16").
         * No `shown`: that flag governs the tray before the die lands, and a flag saying "do not show"
         * travelling beside the value is client-side filtering (ADR-120 §5). */
        target: z.object({ kind: z.enum(['dc', 'ac']), value: z.number().int() }).strict().nullable(),
        mode: RollMode,
        advantage: z.enum(['none', 'adv', 'dis']),
        breakdown: z.string(),
        /**
         * Which side of the fight the actor was on when they rolled, from the encounter's own token
         * (`ResolvedSceneSpec`), resolved on the pod. **Not the same question as `owner`**, and the two
         * came apart the moment a town NPC threw a punch: Bran Hale is `npc:bran_hale`, so `owner` is
         * `npc` for ever, while the board paints his token `hostile` because he is fighting the player —
         * the same combatant red on the board and neutral on his die, in one turn (live round,
         * 2026-09-29). `owner` answers whose die it is; this answers whose side they are on.
         *
         * Absent outside combat, where there is no encounter and nobody is hostile, and absent on every
         * entry written before this field existed — `.optional()` is what lets those parse with no
         * migration. The actor's `ref` is still never on the wire; only the side it resolved to.
         */
        side: Team.optional(),
        dice: z.array(journalDie),
        total: z.number().int(),
        natural: z.union([z.literal(1), z.literal(20)]).optional(),
        entropySource: z.enum(['engine', 'player']),
        autoAfterTimeout: z.boolean(),
        replacedBy: z.string().optional(),
    }),
    entry('AttackResolved', 'combat', {
        attacker: z.string(),
        target: z.string(),
        byPlayer: z.boolean(),
        onPlayer: z.boolean(),
        total: z.number().int(),
        vsAc: z.number().int(),
        hit: z.boolean(),
        crit: z.boolean(),
    }),
    entry('InitiativeRolled', 'combat', {
        order: z.array(z.object({ name: z.string(), isPlayer: z.boolean(), total: z.number().int() }).strict()),
    }),
    entry('CombatStarted', 'combat', { surprise: z.enum(['none', 'player_surprises', 'player_surprised', 'contested_stealth']) }),
    entry('CombatEnded', 'combat', { outcome: z.string(), rounds: z.number().int().min(0), resolution: z.string() }),
    /* ADR-120 §7: damage carries its direction — the target is the player, or it is not. */
    entry('DamageApplied', 'harm', {
        target: z.string(),
        toPlayer: z.boolean(),
        amount: z.number().int().min(0),
        damageType: z.string(),
        /** The player's own hit points after the blow; never an enemy's. */
        hpAfter: z.number().int().optional(),
    }),
    entry('Healed', 'harm', { target: z.string(), toPlayer: z.boolean(), amount: z.number().int().min(0), hpAfter: z.number().int().optional() }),
    entry('ConditionApplied', 'harm', { target: z.string(), toPlayer: z.boolean(), condition: z.string() }),
    entry('ConditionEnded', 'harm', { target: z.string(), toPlayer: z.boolean(), condition: z.string(), reason: z.string() }),
    entry('DeathSaveResolved', 'harm', { total: z.number().int(), successes: z.number().int().min(0).max(3), failures: z.number().int().min(0).max(3) }),
    entry('Stabilized', 'harm', { actor: z.string(), isPlayer: z.boolean() }),
    entry('Died', 'harm', { actor: z.string() }),
    entry('PlayerDied', 'harm', { cause: z.string() }),
    entry('SaveResolved', 'trial', { actor: z.string(), byPlayer: z.boolean(), ability: z.string(), total: z.number().int(), dc: z.number().int(), success: z.boolean() }),
    entry('CheckResolved', 'trial', { actor: z.string(), byPlayer: z.boolean(), key: z.string(), total: z.number().int(), dc: z.number().int(), success: z.boolean() }),
    entry('SpellCast', 'craft', { caster: z.string(), byPlayer: z.boolean(), spellId: z.string(), slotLevel: z.number().int().min(0).max(9) }),
    entry('LevelUp', 'craft', { from: z.number().int().min(1), to: z.number().int().min(2), hpGained: z.number().int().min(0).optional(), hpMax: z.number().int().min(1).optional() }),
    entry('CoinsTransferred', 'purse', { direction: z.enum(['pay', 'receive']), counterpart: z.string(), amountCp: z.number().int().min(0), goldCpAfter: z.number().int().min(0) }),
    entry('ItemsTraded', 'purse', { side: z.enum(['buy', 'sell']), counterpart: z.string(), itemId: z.string(), label: z.string(), qty: z.number().int().min(1), totalCp: z.number().int().min(0), goldCpAfter: z.number().int().min(0) }),
    /* ADR-129: what a defeated enemy left, taken; `from` is the name the player knows. */
    entry('ItemsTaken', 'purse', { from: z.string(), itemId: z.string().nullable(), label: z.string(), qty: z.number().int().min(1) }),
    entry('NegotiationClosed', 'purse', { outcome: z.enum(['deal', 'walkout', 'impasse', 'escalation']) }),
    entry('JourneyCompleted', 'road', { toLabel: z.string(), minutes: z.number().int().min(0) }),
    entry('JourneyInterrupted', 'road', { atLabel: z.string(), toLabel: z.string(), minutes: z.number().int().min(0) }),
    entry('CampPassed', 'road', { minutes: z.number().int().min(0), rested: z.boolean(), hpAfter: z.number().int() }),
    entry('CampInterrupted', 'road', { watch: z.number().int().min(1), minutes: z.number().int().min(0) }),
    entry('NpcApproached', 'road', { name: z.string(), locationLabel: z.string(), how: z.enum(['here', 'seen', 'found']) }),
    entry('SessionStarted', 'sitting', { sessionNumber: z.number().int().min(1) }),
    entry('SessionEnded', 'sitting', { reason: z.string(), playedMinutes: z.number().int().min(0) }),
]);
/**
 * ADR-120 §4: entries arrive when the engine commits them. `replace` is the record so far,
 * sent to a client as it joins (a refresh, a pass typed in again); without it the entries are
 * appended to what the page holds.
 */
export const JournalMessage = z.object({
    type: z.literal('journal'),
    entries: z.array(JournalRecord),
    replace: z.boolean(),
});
/**
 * ADR-120 §6: the table's earlier sittings, on demand (`journal_history` from the client). Read
 * on the actor's own channel, so who may read it is who is already at the table: the pod
 * decides which table from the session's own pass, account or key — never from anything the
 * client names. `entries` are whole sittings, oldest first, each entry carrying its
 * `sessionNumber`; `sittings` counts them; `truncated` says older sittings were left out.
 */
export const JournalHistoryMessage = z.object({
    type: z.literal('journal_history'),
    entries: z.array(JournalRecord),
    sittings: z.number().int().nonnegative(),
    truncated: z.boolean(),
});
/* ------------------------------------------------------------- captions */
/** Captions are always on and synced to TTS sentence boundaries (§1.3). */
export const Caption = z.object({
    type: z.literal('caption'),
    saySeq: z.number().int().nonnegative(),
    turnId: z.string(),
    text: z.string(),
    /**
     * `player` is the player's own line, typed or heard by the voice node, echoed by the pod
     * (ADR-114). Until 2026-09-15 the client echoed only what was typed, so a spoken line
     * never appeared at all.
     */
    speaker: z.enum(['gm', 'npc', 'engine', 'guia', 'player']),
    /**
     * ADR-114: the round the line belongs to. A round opens with a line of the player's;
     * the session's opening is round 0. Absent on a caption from before rounds existed.
     */
    round: z.number().int().nonnegative().optional(),
    /** ADR-085/114: the player's line was an aside to the game master, not a turn. */
    aside: z.boolean().optional(),
    npcRef: z.string().optional(),
    /**
     * What an engine line is about, so the chat marks it as the journal does (ADR-120 §7)
     * without reading its words: the kind of the one event the line states, or `tray` for the
     * prompt that the dice are the player's. Absent on speech, and on an engine line that states
     * no event — a refusal, the budget line, the apology for a failed turn — which the chat marks
     * as neutral. The pod writes it from the event; the client never infers it.
     */
    kind: z.string().optional(),
    lang: Locale,
    /** Server timestamp (epoch ms) at which the sentence began mixing. */
    tStart: z.number(),
    final: z.boolean(),
});
/* ------------------------------------------------- session status/chips */
/**
 * ADR-117: how fast the narrator may be asked to read — xAI's own range is 0.7–1.5
 * (`build_xai_tts_url`), and these are the three the table offers. A table opens at 1.
 */
export const NARRATOR_SPEEDS = [0.7, 1, 1.5];
export const DEFAULT_NARRATOR_SPEED = 1;
export const SessionStatus = z.object({
    type: z.literal('session_status'),
    state: z.enum([
        'lobby',
        'starting',
        'active',
        'awaiting_roll',
        'paused',
        'wrap_up',
        'ending',
        'crashed',
        'recovering',
        'settled',
        'refunded',
    ]),
    seq: z.number().int().nonnegative(),
    /** Minutes of play the credit clock has counted; the client shows no per-turn meter. */
    playedMinutes: z.number().int().min(0),
    /** The discreet ring turns amber at 100 minutes. */
    ringState: z.enum(['calm', 'amber', 'converging']),
    seedCommitment: z.string(),
    seedReveal: z.string().nullable(),
    voice: z.enum(['live', 'reconnecting', 'text_mode', 'backup_voice']),
    /** ADR-114: what the table's pass has spent of its budget, this session included; absent without a pass. */
    budget: z.object({ spentUsd: z.number().nonnegative(), capUsd: z.number().positive() }).optional(),
    /**
     * ADR-117: the player silenced the narrator. The pod stops synthesising — the table
     * reads instead of listening, and the budget stops paying for speech — until the
     * player asks for the voice back. On the status so a page that comes back knows
     * which way the button points.
     */
    narratorMuted: z.boolean().default(false),
    /** ADR-117: how fast the narrator is reading, so a page that comes back says the same. */
    narratorSpeed: z.number().positive().default(DEFAULT_NARRATOR_SPEED),
    /**
     * ADR-126: the player paused the narration. The node holds its audio where it stands and
     * the client holds its reveal; the game does not stop — the session clock runs, a tray's
     * ten seconds run. On the status so a page that comes back knows which way the button points.
     */
    narratorPaused: z.boolean().default(false),
    /**
     * ADR-128 decision 4: a recording is `locked` (open, hands-free) or `parked` (the minute stopped
     * it, or it is waiting to be sent) — while either holds the client's text field is disabled, so
     * two utterances never compete for one turn. `none` otherwise.
     */
    /* ADR-128 decision 3: `preview` — a held recording's transcript is in the field with the clock running. */
    recording: z.enum(['none', 'locked', 'parked', 'preview']).default('none'),
    /**
     * §6.6 "enemy dice": whether the table shows an enemy's die as a 400 ms tumble. The roll
     * itself reaches the client on the journal (ADR-120, `owner` filled); this only says whether
     * to draw it. The setting is the session's (`SessionSettings.showEnemyDice`), carried here so
     * the client that decorates the roll knows, and a page that comes back knows the same.
     */
    showEnemyDice: z.boolean().default(false),
});
/**
 * The EU AI Act art. 50(1) disclosure, on the wire (ADR-058).
 *
 * In force since 2026-08-02: a person interacting with an AI system must be
 * informed of it, at the first interaction. §8.6 puts the notice at account
 * creation, which is where a *screen* belongs — it is a signup gate rather than
 * an onboarding beat, and it must never sit between a returning player and their
 * session. This message is the other half: the same fact, machine-readable,
 * carried by every session so a client, an audit and a recording all have it
 * without depending on what a signup flow did months earlier.
 *
 * It names the system rather than gesturing at one. `narrationProvider` and
 * `narrationModel` are what actually wrote the sentences the player is about to
 * hear, and `syntheticAudio` says whether the voice is generated — art. 50(2)'s
 * marking obligation attaches to that and not to the text.
 *
 * `emotionRecognition` is `false` in v1 and is here because the notice it would
 * trigger is a different obligation with a different text: M2's speech-emotion
 * classes turn this into an art. 50(3) case. Carrying the field now means the day
 * that ships is a value change rather than a protocol change — and, per ADR-058,
 * the signal is described as **acoustic prosody, never emotion**, because the
 * system's purpose is the legal test and a field name is evidence of it.
 */
export const AiDisclosure = z.object({
    type: z.literal('ai_disclosure'),
    /** The catalog id of the player-visible text; §9.3 keeps the words out of code. */
    messageKey: z.string(),
    narrationProvider: z.string(),
    narrationModel: z.string(),
    /** True once the voice leg speaks: the audio is generated, not recorded. */
    syntheticAudio: z.boolean(),
    /**
     * How synthetic audio is marked, when it is. `declared` is a metadata claim on
     * the stream; `watermark` would be a signal in the samples that survives
     * re-recording. Only the first is implemented, and the distinction is kept on
     * the wire so nothing downstream can read a declaration as a watermark.
     */
    audioMarking: z.enum(['none', 'declared', 'watermark']),
    /** False in v1. M2's prosody classes make this an art. 50(3) notice as well. */
    emotionRecognition: z.boolean(),
});
/* ------------------------------------------------- server → client union */
export const HudUpdate = z.object({
    type: z.literal('hud'),
    seq: z.number().int().nonnegative(),
    hud: HudModel,
});
export const ChoicesOffered = z.object({
    type: z.literal('choices'),
    /** Empty clears the chips: sent at the top of every turn, so no scene keeps the last one's options (2026-09-07). */
    options: z.array(z.string().max(60)).min(0).max(4),
});
/** ADR-085: how many asides the player has left in this scene, sent after each one. */
export const AsideBudget = z.object({
    type: z.literal('aside_budget'),
    left: z.number().int().min(0),
    max: z.number().int().min(1),
});
export const ToastMessage = z.object({
    type: z.literal('toast'),
    /** Localized HUD toast for a `RulesError`; it never reaches the voice (§5.3). */
    messageKey: z.string(),
    slots: z.record(z.string(), z.union([z.string(), z.number()])).default({}),
    severity: z.enum(['info', 'warning', 'error']),
});
export const CueMessage = z.object({
    type: z.literal('cue'),
    kind: z.enum(['music_stem', 'weather', 'lighting']),
    id: z.string(),
    intensity: z.number().int().min(1).max(3),
});
/**
 * ADR-103: the turn's lifecycle, so the client can hold its input while the table is
 * answering. `started` when the pod takes an utterance or an action; `ended` when the
 * turn has been narrated, or parked on a roll, or refused.
 */
export const TurnPhase = z.object({
    type: z.literal('turn'),
    turnId: z.string(),
    /**
     * ADR-128 decision 2: `heard` is the narration of this turn heard to the end — the node's
     * `say_played` for it — or never going to be heard at all: a text table, a muted or silent
     * voice, a turn that handed the voice nothing, a cancelled one. It follows `ended`, at once
     * where there is nothing to wait for. The client blocks the microphone between `started` and
     * `heard`; `ended` alone says the pod is done writing, which is not the same thing.
     */
    phase: z.enum(['started', 'ended', 'heard']),
});
/**
 * ADR-127 decision 1 (issue #63): the player's words asked to end the night. The pod ends
 * nothing on the words alone — a typed, transcribed or negated "encerrar a sessão" used to end
 * the paid sitting at once — and asks instead; the client shows the exit button's own
 * confirmation, and a yes comes back as `end_session`. A no is nothing.
 */
export const ConfirmEnd = z.object({
    type: z.literal('confirm_end'),
    turnId: z.string(),
});
/**
 * ADR-128 decision 2: a line the pod hands back to the player's text field — the utterance
 * that was waiting for the narrator when a second one arrived and took its place. Nothing of
 * it was lost: it is theirs to send again, as the "não foi isso que eu disse" line is.
 */
export const Draft = z.object({
    type: z.literal('draft'),
    /** ADR-128 decision 3: an empty draft empties the field — a previewed line was sent as it stood. */
    text: z.string(),
    /**
     * ADR-128 decision 4 (amended 2026-10-30): a resumed segment of a locked recording. The client
     * appends `text` to whatever its field holds at that moment (`appendSegment`) — the earlier
     * segments and the player's edits during the pause — and replaces nothing. Absent: the draft
     * replaces the field, as every other draft does.
     */
    append: z.boolean().optional(),
});
/**
 * ADR-112: a campaign's prologue — the leaflet the first session of a campaign opens
 * on, read aloud page by page before the first scene. Sent on session one only, in the
 * session's language; `voiced` says whether the narrator will read it, so a text-only
 * table turns the pages by itself.
 */
export const ProloguePage = z.object({
    index: z.number().int().min(0),
    /** A path the web client serves (`/prologue/<campaign>/<n>.webp`); absent draws the leaflet without art. */
    image: z.string().optional(),
    alt: z.string().default(''),
    /** The page as read, delivery marks removed. */
    text: z.string().min(1),
    /** The page read ahead of time by this session's narrator voice, in its language: a static file the client plays. */
    audio: z.object({ src: z.string().min(1), seconds: z.number().positive() }).optional(),
});
export const PrologueMessage = z.object({
    type: z.literal('prologue'),
    campaignId: z.string(),
    title: z.string().min(1),
    pages: z.array(ProloguePage).min(1),
    voiced: z.boolean(),
    /**
     * Every page has a recording for this table: the client plays the files, turns each
     * page when its audio ends and says `finished`; nothing is synthesised for it. False,
     * a voiced table falls back to the narrator reading live.
     */
    recorded: z.boolean().default(false),
    /**
     * ADR-118: what the character is trying to do tonight, in the table's language. The
     * leaflet told the player about the world and nothing about the evening, so the
     * objective was first read in the HUD, after play had started (playtest report #21).
     * Absent where the campaign's opening authored none.
     */
    objective: z.string().optional(),
});
/** ADR-112: the narrator finished reading a page live; the leaflet turns to `index`. A recorded prologue never sends it. */
export const ProloguePageTurn = z.object({
    type: z.literal('prologue_page'),
    index: z.number().int().min(0),
});
/** ADR-112: the prologue is over — read to the end or skipped — and the first scene opens. */
export const PrologueEnd = z.object({
    type: z.literal('prologue_end'),
    reason: z.enum(['finished', 'skipped']),
});
/**
 * ADR-114: every caption of the session so far, sent to a client as it joins — a refreshed
 * page, a second tab, a pass typed in again — so the record does not start blank.
 */
export const TranscriptHistory = z.object({
    type: z.literal('transcript'),
    captions: z.array(Caption),
});
export const ServerMessage = z.discriminatedUnion('type', [
    HudUpdate,
    CharacterSheet,
    CampaignInfo,
    QuestLog,
    TranscriptHistory,
    JournalMessage,
    JournalHistoryMessage,
    BoardDiffBatch,
    WatermarkRelease,
    DiceTrayRequest,
    DiceTrayResolved,
    Caption,
    SessionStatus,
    ChoicesOffered,
    AsideBudget,
    ToastMessage,
    CueMessage,
    AiDisclosure,
    TurnPhase,
    ConfirmEnd,
    Draft,
    PrologueMessage,
    ProloguePageTurn,
    PrologueEnd,
]);
/* ------------------------------------------------- client → server union */
export const PlayerUtterance = z.object({
    type: z.literal('utterance'),
    /** Typed text; a typed turn is a voice turn minus tone tags (§7.4). */
    text: z.string().min(1),
    ooc: z.boolean().default(false),
    /**
     * An aside to the game master (ADR-085): what the character can see, hear or already
     * knows, answered without a turn passing. Budgeted per scene; the pod says how many are left.
     */
    aside: z.boolean().default(false),
});
export const PlayerAction = z.object({
    type: z.literal('action'),
    /** A command kind from the action bar; the server validates it (§5.3). */
    command: z.string(),
    payload: z.unknown(),
});
/** ADR-120 §6: the client asks for the table's earlier sittings. It names nothing: the pod knows whose table this is. */
export const ClientJournalHistory = z
    .object({
    type: z.literal('journal_history'),
})
    .strict();
export const ClientReconnect = z.object({
    type: z.literal('reconnect'),
    sessionToken: z.string(),
    lastSeq: z.number().int().nonnegative(),
});
export const ToneCorrection = z.object({
    type: z.literal('tone_correction'),
    checkSeq: z.number().int().nonnegative(),
});
export const ResultStylePreference = z.object({
    type: z.literal('result_style'),
    style: ResultLineStyle,
});
/**
 * The X-card (ADR-063 §5). Its own message rather than an `action`, because it
 * must reach the server on a turn that is otherwise busy — the action bar is
 * disabled while a roll is pending and while a turn is resolving, and a safety
 * tool that can be locked out by the state machine is not one.
 */
/**
 * A line drawn during play (ADR-063 §5).
 *
 * Session zero writes the campaign's lines; this adds one without ending the
 * session, because the moment a player discovers a line is usually the moment
 * they meet it. Tags only — the same vocabulary the pack's storylets carry, so
 * the director can filter on them — and never free text, which would be a
 * request the engine could not enforce.
 */
export const SafetyLine = z.object({
    type: z.literal('safety_line'),
    tags: z.array(z.string().min(1).max(40)).min(1).max(20),
});
export const SafetyCard = z.object({
    type: z.literal('safety_card'),
    turnId: z.string().default(''),
    source: z.enum(['voice', 'button', 'text']).default('button'),
});
/**
 * ADR-112: the client's side of the prologue. `begin` once the leaflet is on screen and
 * the voice leg has settled (`voiced` false when the table has no voice after all);
 * `skip` when the player closes it; `finished` when a text-only table has turned the
 * last page.
 */
export const PrologueControl = z.object({
    type: z.literal('prologue'),
    action: z.enum(['begin', 'skip', 'finished']),
    voiced: z.boolean().default(true),
});
/**
 * ADR-117: the narrator's voice, from the player's side. `mute` stops the sentence
 * being spoken now and every one after it — the pod cancels the open utterance and
 * synthesises nothing while it holds — and `unmute` gives the voice back from the next
 * narration on. Captions are unaffected: a silenced table still reads the story as it
 * is written.
 */
export const NarratorVoice = z.object({
    type: z.literal('voice'),
    /** ADR-126: `pause` and `resume` hold the narration as one — text and voice — and nothing else. */
    action: z.enum(['mute', 'unmute', 'speed', 'pause', 'resume']),
    /**
     * ADR-117: how fast the narrator reads, on `speed`. Only the three the table offers —
     * a number in between validated here and was then ignored by the pod, which is a
     * client left believing something happened (CodeRabbit on PR #26). A `speed` action
     * without one changes nothing at all.
     */
    speed: z.union([z.literal(NARRATOR_SPEEDS[0]), z.literal(NARRATOR_SPEEDS[1]), z.literal(NARRATOR_SPEEDS[2])]).optional(),
});
/**
 * ADR-119: feet or metres, from the player's side. It takes effect on everything said
 * from then on — a line already on the record keeps the words it was written with.
 */
export const MeasuresPreference = z.object({
    type: z.literal('measures'),
    measures: Measures,
});
/**
 * ADR-127 decision 1: the yes to `confirm_end`. The session ends through the same path as
 * every other end and the player stays at the table — the summary opens when it settles.
 * Not the exit button's leave, which navigates away.
 */
export const EndSessionRequest = z.object({
    type: z.literal('end_session'),
});
/**
 * Issue #53: the player cancelled the voice message they were recording — a slide off the
 * button, Escape under the space bar. The node cannot be told (the browser has no channel
 * to it but the audio), so the pod marks the utterance in progress — the one whose
 * `user_turn{start}` it last received — and drops the `final` that closes it: no seconds
 * counted, no turn, no echo. No timestamp travels here: the node's clock and the browser's
 * are not the same clock, and the mark needs neither.
 */
export const CancelRecording = z.object({
    type: z.literal('cancel_recording'),
});
/**
 * ADR-128 decision 4 (#68): the locked recording. `lock` — the player slid up to the padlock or
 * double-pressed the space bar; the pod tells the node to hold its silence gate. `stopped` — the
 * player paused, or the minute ran out: the client closed the microphone; the pod lifts the lock
 * and **parks** the utterance's `final` — it lands in the field as a `draft` and waits. `resume`
 * (amended 2026-10-30) — the player reopens the same locked recording: the pod puts the lock back
 * **before** the node opens the next utterance, so that utterance's `final` is a segment appended
 * to the parked message (`draft.append`) and not a recording that replaces it. A fresh hold sends
 * no `resume`: its `start` reaches the pod with no lock on, and drops what was parked, as decided.
 * `send` — the parked utterance, or the one still to arrive, becomes the turn. The trash is
 * `cancel_recording`. Nothing is sent without the player, and a parked message never expires — it
 * waits, or ends with the session.
 */
export const RecordingControl = z.object({
    type: z.literal('recording'),
    /* `edit` (ADR-128 decision 3): the player touched the previewed line; the clock stops and the send waits for the field. */
    action: z.enum(['lock', 'resume', 'stopped', 'send', 'edit']),
});
export const ClientMessage = z.discriminatedUnion('type', [
    PlayerUtterance,
    NarratorVoice,
    MeasuresPreference,
    EndSessionRequest,
    CancelRecording,
    RecordingControl,
    PlayerAction,
    DiceTrayThrow,
    DiceTrayLanded,
    SetRollModeMessage,
    ClientReconnect,
    ClientJournalHistory,
    ToneCorrection,
    ResultStylePreference,
    SafetyCard,
    SafetyLine,
    PrologueControl,
]);
/* ------------------------------------------------------------ constants */
/** Cover glyphs the targeting indicator renders (§6.4). */
export const COVER_GLYPH = Object.freeze({
    none: '',
    half: '½',
    three_quarters: '¾',
    total: '×',
});
//# sourceMappingURL=client.js.map